import { LoanFormConfigDTO } from '@/shared/api/model';
import { DEFAULT_STAMP_CONFIG, DEFAULT_TEXT_CONTENT, HAZINA_DEDUCTION_FORM_CONFIG } from './DeductionFormConfig';

export interface LoanApplicationTextContent {
    referencePrefix?: string;
    applicationReferenceLabel?: string;
    applicationDateLabel?: string;
    headerSubtitle?: string;
    personalSectionTitle?: string;
    loanSectionTitle?: string;
    purposeSectionTitle?: string;
    purposePrompt?: string;
    collateralSectionTitle?: string;
    collateralSummary?: string;
    collateralDescription?: string;
    collateralCommitment?: string;
    penaltySectionTitle?: string;
    penaltyText?: string;
    accountSectionTitle?: string;
    accountPrompt?: string;
    insuranceSectionTitle?: string;
    insuranceText?: string;
    officeUseSectionTitle?: string;
    officeUseApprovedDateLabel?: string;
    officeUseApprovedAmountLabel?: string;
    officeUseApprovedPeriodLabel?: string;
    officeUseInterestLabel?: string;
    officeUseApprovalListTitle?: string;
    approvalTableNameLabel?: string;
    approvalTableSignatureLabel?: string;
    approvalTableDateLabel?: string;
    applicantSignatureLabel?: string;
    signatureDateLabel?: string;
    monthlyInstallmentLabel?: string;
    disbursementDateLabel?: string;
    maturityDateLabel?: string;
    interestRateLabel?: string;
    totalPrincipalLabel?: string;
    totalInterestLabel?: string;
    totalRepaymentLabel?: string;
    applicantDeclarationTitle?: string;
    applicantDeclarationText?: string;
    footerPrimary?: string;
    footerSecondary?: string;
}

export interface LoanApplicationDisplayConfig {
    approvalRows?: number;
    showPenaltySection?: boolean;
    showInsuranceSection?: boolean;
    showInterestRate?: boolean;
    showDisbursementDate?: boolean;
    showMaturityDate?: boolean;
    showTotalPrincipal?: boolean;
    showTotalInterest?: boolean;
    showTotalRepayment?: boolean;
}

export interface LoanApplicationTemplateConfig extends LoanFormConfigDTO {
    deductionForm?: typeof HAZINA_DEDUCTION_FORM_CONFIG;
    applicationForm?: {
        textContent?: LoanApplicationTextContent;
        display?: LoanApplicationDisplayConfig;
    };
}

export const DEFAULT_LOAN_APPLICATION_TEXT_CONTENT: Required<LoanApplicationTextContent> = {
    referencePrefix: 'LOAN',
    applicationReferenceLabel: 'Application Reference',
    applicationDateLabel: 'Application Date',
    headerSubtitle: 'Member Loan Request',
    personalSectionTitle: 'APPLICANT INFORMATION',
    loanSectionTitle: 'LOAN DETAILS',
    purposeSectionTitle: 'LOAN PURPOSE',
    purposePrompt: 'State briefly the main purpose of this loan.',
    collateralSectionTitle: 'DECLARATION AND SECURITY',
    collateralSummary: 'The borrower confirms that the information provided in this form is true and complete.',
    collateralDescription: 'The applicant agrees that any approved facility will be governed by the institution loan terms and repayment rules.',
    collateralCommitment: 'If employment status changes or salary deductions are interrupted, recovery may continue through savings, shares, benefits, or any other lawful recovery channel.',
    penaltySectionTitle: 'PENALTY',
    penaltyText: 'Late payment may attract additional charges according to the approved product rules.',
    accountSectionTitle: 'RECEIVING ACCOUNT',
    accountPrompt: 'Please provide the account number or bank details to receive the approved loan.',
    insuranceSectionTitle: 'INSURANCE / LOAN COVER',
    insuranceText: 'Where applicable, loan insurance and processing charges may be deducted before disbursement.',
    officeUseSectionTitle: 'FOR OFFICIAL USE ONLY',
    officeUseApprovedDateLabel: 'Approved Date',
    officeUseApprovedAmountLabel: 'Approved Amount',
    officeUseApprovedPeriodLabel: 'Approved Period',
    officeUseInterestLabel: 'Interest',
    officeUseApprovalListTitle: 'Approved by',
    approvalTableNameLabel: 'Name',
    approvalTableSignatureLabel: 'Signature',
    approvalTableDateLabel: 'Date',
    applicantSignatureLabel: 'Applicant Signature',
    signatureDateLabel: 'Date',
    monthlyInstallmentLabel: 'Monthly installment',
    disbursementDateLabel: 'Expected disbursement date',
    maturityDateLabel: 'Expected maturity date',
    interestRateLabel: 'Interest rate',
    totalPrincipalLabel: 'Total principal',
    totalInterestLabel: 'Total interest',
    totalRepaymentLabel: 'Total repayment',
    applicantDeclarationTitle: 'APPLICANT DECLARATION',
    applicantDeclarationText: 'I declare that the information given above is correct and I agree to comply with the loan terms and conditions of the institution.',
    footerPrimary: 'Fill one copy only.',
    footerSecondary: 'Attach this form together with your recent salary slips or other supporting income documents as required.',
};

export const DEFAULT_LOAN_APPLICATION_FORM_CONFIG: LoanApplicationTemplateConfig = {
    language: 'EN',
    header: {
        showLogo: true,
        formTitle: 'LOAN APPLICATION FORM',
    },
    displayOptions: {
        includeFeeBreakdown: false,
        includeRepaymentSchedule: false,
        includeGuarantorInfo: false,
        includeSignatures: true,
        includeTermsAndConditions: false,
    },
    branding: {
        showWatermark: false,
    },
    deductionForm: {
        branding: {
            showWatermark: false,
        },
        stamp: DEFAULT_STAMP_CONFIG,
        textContent: DEFAULT_TEXT_CONTENT,
    },
    applicationForm: {
        display: {
            approvalRows: 3,
            showPenaltySection: true,
            showInsuranceSection: true,
            showInterestRate: true,
            showDisbursementDate: true,
            showMaturityDate: true,
            showTotalPrincipal: true,
            showTotalInterest: true,
            showTotalRepayment: true,
        },
        textContent: DEFAULT_LOAN_APPLICATION_TEXT_CONTENT,
    },
};

export const HAZINA_LOAN_APPLICATION_FORM_CONFIG: LoanApplicationTemplateConfig = {
    language: 'SW',
    header: {
        showLogo: true,
        companyName: 'HAZINA SAVING AND CREDIT COOPERATIVE SOCIETY LTD',
        companyAddress: 'Madaraka Avenue, P.O.BOX 9111, Dar Es Salaam',
        companyPhone: '0710 933944, 0710606605',
        companyEmail: 'info@hazinasaccos.co.tz',
        formTitle: 'FOMU YA MAOMBI YA MKOPO',
    },
    displayOptions: {
        includeFeeBreakdown: false,
        includeRepaymentSchedule: false,
        includeGuarantorInfo: false,
        includeSignatures: true,
        includeTermsAndConditions: false,
    },
    branding: {
        logoUrl: '/images/hazina-logo.png',
        watermarkUrl: '/images/hazina-logo.png',
        showWatermark: true,
    },
    deductionForm: HAZINA_DEDUCTION_FORM_CONFIG,
    applicationForm: {
        display: {
            approvalRows: 3,
            showPenaltySection: true,
            showInsuranceSection: true,
            showInterestRate: true,
            showDisbursementDate: true,
            showMaturityDate: true,
            showTotalPrincipal: true,
            showTotalInterest: true,
            showTotalRepayment: true,
        },
        textContent: {
            referencePrefix: 'HSCS',
            applicationReferenceLabel: 'Kumbukumbu',
            applicationDateLabel: 'Tarehe',
            headerSubtitle: '(Maendeleo, Biashara, Ujenzi, na Standing Order)',
            personalSectionTitle: '1. MAELEZO BINAFSI (Ijazwe Na Mwombaji)',
            loanSectionTitle: '2. MAOMBI YA MKOPO',
            purposeSectionTitle: '3. SABABU YA MAOMBI YA MKOPO',
            purposePrompt: 'Eleza kwa kifupi madhumuni hasa ya mkopo huu.',
            collateralSectionTitle: '4. DHAMANA YA MKOPO',
            collateralSummary: 'Dhamana ya mkopo huu ni AKIBA zangu zilizopo Hazina SACCOS.',
            collateralDescription: 'Ninathibitisha kuwa maelezo niliyotoa hapo juu ni ya kweli na hivyo basi kuulipa mkopo huu pamoja na riba hakuwezi kuniletea matatizo kifedha. Ninakubaliana na sheria zote zinazohusu mkopo na makato yake.',
            collateralCommitment: 'Iwapo kutajitokeza tatizo lolote la kupelekea kushindwa kurejesha mkopo huu (kuachishwa au kuacha kazi) marejesho ya mkopo huo yafanyike kupitia Akiba, Hisa na Mafao yangu.',
            penaltySectionTitle: '5. ADHABU',
            penaltyText: 'Mwanachama akichelewesha rejesho atakatwa adhabu ya 1% ya rejesho la mwezi husika.',
            accountSectionTitle: '6. AKAUNTI YAKO ITAKAYOPOKELEA',
            accountPrompt: 'Tafadhali jaza akaunti namba yako kwa usahihi pamoja na jina la benki.',
            insuranceSectionTitle: '7. MAKATO',
            insuranceText: 'Kutoka kwenye kiwango ulichoomba utakatwa 1% bima na 1% ada ya kuchakata mkopo.',
            officeUseSectionTitle: '8. KWA MATUMIZI YA OFISI TU',
            officeUseApprovedDateLabel: 'Umepitishwa Tarehe',
            officeUseApprovedAmountLabel: 'Umeidhinishwa Mkopo wa Tsh',
            officeUseApprovedPeriodLabel: 'Utakaolipwa kwa kipindi cha miezi',
            officeUseInterestLabel: 'Na riba TSH',
            officeUseApprovalListTitle: 'Kamati ya mkopo ya watendaji waliopitisha mkopo huu',
            approvalTableNameLabel: 'Jina',
            approvalTableSignatureLabel: 'Sahihi',
            approvalTableDateLabel: 'Tarehe',
            applicantSignatureLabel: 'Saini ya mwombaji',
            signatureDateLabel: 'Tarehe',
            monthlyInstallmentLabel: 'Kiasi cha kulipa kila mwezi',
            disbursementDateLabel: 'Tarehe ya kutolewa',
            maturityDateLabel: 'Tarehe ya mwisho',
            interestRateLabel: 'Riba',
            totalPrincipalLabel: 'Jumla ya msingi',
            totalInterestLabel: 'Jumla ya riba',
            totalRepaymentLabel: 'Jumla ya marejesho',
            applicantDeclarationTitle: 'UTHIBITISHO WA MWOMBAJI',
            applicantDeclarationText: 'Ninakiri kuwa taarifa zote nilizotoa ni sahihi na nakubali kuzingatia masharti yote ya mkopo yatakayotolewa na Hazina SACCOS.',
            footerPrimary: '***Jaza Nakala Moja tu***',
            footerSecondary: '***Ambatanisha fomu hii pamoja na salary slip za miezi mitatu (3) ya mishahara yako ya karibuni***',
        },
    },
};

export const stringifyLoanApplicationFormConfig = (config: LoanApplicationTemplateConfig) => JSON.stringify(config, null, 2);
